"use client";

import classNames from "classnames";
import DiscountsDropDown from "components/DiscountsDropdown";
import Divider from "components/divider";
import { Rs } from "constants/common";
import { Container, Typography } from "ct-design";
import SuccessTick from "icons/successTick.svg";
import Image from "next/image";
import { FunctionComponent, useCallback, useMemo } from "react";
import { fareCategory } from "src/containers/chart/chartUtils";
import { FareBreakupState } from "src/containers/itinerary/types";
import { CouponData } from "src/context/itineraryContext/types";
import { formatFare } from "src/utils/generalUtils";

export enum FLOW {
  "PRE" = "pre",
  "POST" = "post",
}
interface FareBreakupProps {
  fareBreakup: FareBreakupState;
  couponData?: CouponData | null;
  isSticky?: boolean;
  top?: `${string}px`;
  totalTitle?: string;
  flow?: "pre" | "post";
  totalFare?: number;
  showNonRefundable?: boolean;
  nonRefundableData?: string[];
}

const iconsForLabels = {
  CT_PASS_DIS:
    "https://static-assets-ct.flixcart.com/ct/assets/resources/images/bus/busPass/BlueBusPassIcon.svg",
};

const FareBreakup: FunctionComponent<FareBreakupProps> = (props) => {
  const {
    fareBreakup,
    couponData,
    isSticky = false,
    top = "0px",
    totalTitle = "Total amount payable",
    flow = FLOW.PRE,
    totalFare,
    nonRefundableData,
  } = props;

  const getTotalDiscounts = useCallback(
    (filterCondition: any, mapCondition: any) => {
      let totalAdditionDiscounts = 0;
      const discountArray = Object.keys(fareBreakup.fare)
        .filter((item) => item.includes("DIS"))
        .map((item) => fareBreakup.fare[item])
        .filter(filterCondition);

      discountArray.forEach((item) => {
        totalAdditionDiscounts += mapCondition(item);
      });
      return totalAdditionDiscounts;
    },
    [fareBreakup.fare]
  );

  //Here we want the supliier discount and Bus pass discount also which is in negative
  const totalDiscounts = useMemo(
    () =>
      getTotalDiscounts(
        (value: number) => value,
        (item: number) => Math.abs(item)
      ),
    [getTotalDiscounts]
  );

  //Here we only want the coupon and supercoin discount that will be positive as CT_TF is inclusive of other discounts
  const getTotalFare = useMemo(() => {
    if (flow === FLOW.PRE) {
      const _totalFare =
        fareBreakup?.fare.CT_TF -
        getTotalDiscounts(
          (value: number) => value > 0,
          (item: number) => item
        );

      return formatFare(Math.round(_totalFare));
    } else if (totalFare) return formatFare(Math.round(totalFare));
  }, [fareBreakup?.fare.CT_TF, flow, getTotalDiscounts, totalFare]);

  const wrapperClass = classNames("ba-solid bc-line-500 br-16", {
    "p-sticky bg-neutral-100 z-100": isSticky,
  });

  const getIcon = (key: string): string => {
    return iconsForLabels[key as keyof typeof iconsForLabels];
  };

  return (
    <>
      <Container
        className={wrapperClass}
        css={{ overflow: "hidden" }}
        top={isSticky ? top : "0"}
      >
        <Container className=" pt-4 px-4 w-100p" css={{ maxWidth: "288px" }}>
          <Typography variant="HM2">Price breakup</Typography>

          <Container className="my-4">
            {Object.keys(fareBreakup.fare).map((key, index) => {
              if (
                !fareCategory.SKIP_FROM_ITINERARY.includes(key) &&
                !key.includes("DIS") &&
                (fareBreakup.fare[key] > 0 ||
                  fareBreakup.fare[key] < 0 ||
                  fareCategory.SHOW_ALWAYS.includes(key))
              )
                return (
                  <Container
                    key={index}
                    className="mb-4 flex flex-between"
                    rowGap={"20px"}
                  >
                    <Container className="flex flex-middle">
                      <Typography variant="B2">
                        {fareBreakup.fareLabel[key]}
                        {key === (FLOW.PRE === flow ? "SUP_BF" : "BF")
                          ? ` (${fareBreakup.seats} traveller${
                              fareBreakup.seats > 1 ? "s" : ""
                            })`
                          : ""}
                        {nonRefundableData?.includes(
                          fareBreakup.fareCategory[key]
                        )
                          ? "*"
                          : ""}
                      </Typography>
                      {iconsForLabels[key as keyof typeof iconsForLabels] ? (
                        <Image
                          width={15}
                          height={12}
                          className="ml-2"
                          src={getIcon(key)}
                          alt="label-icon"
                        />
                      ) : null}
                    </Container>

                    <Container className="w-30p ta-right">
                      <Typography
                        variant="B2"
                        color={key.includes("DIS") ? "success" : "primary"}
                      >
                        {key.includes("DIS") ? "-" : ""}
                        {Rs}
                        {formatFare(
                          Math.round(Math.abs(fareBreakup.fare[key]))
                        )}
                      </Typography>
                    </Container>
                  </Container>
                );
            })}

            {totalDiscounts > 0 ? (
              <DiscountsDropDown
                fareData={fareBreakup}
                totalDiscounts={totalDiscounts}
              />
            ) : null}

            <Container
              css={{ borderTop: "1px dotted #e6e6e6" }}
              className="pt-4 flex flex-between flex-middle"
            >
              <Typography variant="HM4">{totalTitle}</Typography>
              <Typography variant="HM2">
                {Rs}
                {getTotalFare}
              </Typography>
            </Container>
          </Container>

          {nonRefundableData?.length ? (
            <Container className="pb-3">
              <Divider style="dotted" />
              <Typography variant="B3" className="mt-3" color="subheading">
                *Non refundable charges
              </Typography>
            </Container>
          ) : null}
        </Container>

        {(couponData?.amount && couponData.amount > 0) ||
        couponData?.amountSaved > 0 ? (
          <Container
            className="px-4 py-2 flex flex-middle"
            backgroundColor="#ECF8F4"
          >
            <SuccessTick />

            <Typography variant="B2" className="ml-1" color="success">
              ₹{couponData?.amount || couponData?.amountSaved} saved with{" "}
              {couponData?.couponCode}
            </Typography>
          </Container>
        ) : null}
      </Container>
    </>
  );
};

export default FareBreakup;
